import { useRouteContext } from '@/app/layouts/TenantLayout'
import { CreateProjectDialog } from '@/app/pages/Projects/CreateProjectDialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { isPlatformUser } from '@/stores/auth'
import { useAuthStore, useCurrentOrganizationIdFromState, useCurrentTenant } from '@/stores/authStore'
import { useCanCreateProject } from '@/utils/permissions'
import { Box, ChevronsUpDown, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { ProjectSwitcher } from './ProjectSwitcher'
import { resolveEffectiveProjectId } from './switcherUtils'
import { useDefaultProject, useProjects } from './useSwitcherData'

interface ProjectSwitcherDropdownProps {
	onProjectSelect?: (tenantId: string) => void
	onNavigate: () => void
	isActive?: boolean
}

export function ProjectSwitcherDropdown({ onProjectSelect, onNavigate, isActive }: ProjectSwitcherDropdownProps) {
	const [open, setOpen] = useState(false)
	const [showCreateDialog, setShowCreateDialog] = useState(false)
	const { orgId, tenantId } = useRouteContext()
	const { tenants } = useProjects({ orgIdOverride: orgId })
	const hasProjects = tenants.length > 0
	const canCreateProject = useCanCreateProject()
	const defaultProjectId = useDefaultProject(orgId ?? null)
	const currentTenant = useCurrentTenant()
	const tenantList = useAuthStore(s => (s.user && isPlatformUser(s.user) ? s.user.tenant_list : null))
	// Mirrors useProjects' own fallback, so the org used to validate currentTenant is the one that
	// filtered `tenants` in the first place.
	const orgIdFromState = useCurrentOrganizationIdFromState()

	const effectiveProjectId = useMemo(
		() =>
			resolveEffectiveProjectId({
				routeTenantId: tenantId,
				currentTenant,
				orgTenants: tenants,
				tenantList,
				orgId: orgId ?? orgIdFromState,
				defaultProjectId,
			}),
		[tenantId, currentTenant, tenants, tenantList, orgId, orgIdFromState, defaultProjectId],
	)
	// The active project can be missing from the org-filtered list (root project, org/tenant
	// mismatch in the URL, tenant_list stale after a failed /me). The route id is authoritative —
	// that project is on screen; a pinned/stale id that resolves to nothing is not.
	const effectiveProjectName = useMemo(() => {
		if (!effectiveProjectId) {
			return null
		}
		const inOrg = tenants.find(t => t.id === effectiveProjectId)
		if (inOrg) {
			return inOrg.name
		}
		const known = tenantList?.find(t => t.id === effectiveProjectId)
		if (known) {
			return known.name || known.id
		}
		return effectiveProjectId === tenantId ? effectiveProjectId : null
	}, [effectiveProjectId, tenantId, tenants, tenantList])

	const hasSelection = effectiveProjectName !== null

	const displayName =
		effectiveProjectName ?? (hasProjects ? 'Select Project' : canCreateProject ? 'Create Project' : 'No Projects')

	const handleMainClick = () => {
		if (hasProjects || hasSelection) {
			onNavigate()
		} else if (canCreateProject) {
			setShowCreateDialog(true)
		}
	}

	return (
		<>
			<Popover open={open} onOpenChange={setOpen}>
				<div
					className={cn(
						'group/tab flex items-center w-full rounded-md transition-all duration-200',
						open || isActive
							? 'bg-[var(--switcher-active-bg)] border border-[var(--switcher-active-border)] shadow-[var(--switcher-active-shadow)] text-sidebar-active-text'
							: 'text-sidebar-inactive-text hover:text-sidebar-active-text hover:bg-sidebar-accent/50 border border-transparent',
					)}>
					<button
						type='button'
						className='flex items-center gap-2 px-2 py-1.5 flex-1 min-w-0 cursor-pointer'
						onClick={handleMainClick}>
						{hasProjects || hasSelection ? (
							<Box className='size-(--icon-standalone) shrink-0 ml-2' />
						) : (
							<Plus className='size-(--icon-standalone) shrink-0 ml-2' />
						)}
						<span
							className={cn(
								'truncate text-control flex-1 text-left',
								isActive ? 'text-sidebar-active-text' : 'text-sidebar-inactive-text',
							)}>
							{displayName}
						</span>
					</button>
					<PopoverTrigger asChild>
						<button
							type='button'
							className={cn(
								'shrink-0 flex items-center justify-center min-w-8 min-h-8 rounded-md cursor-pointer transition-opacity',
								open ? 'opacity-50' : 'opacity-0 group-hover/tab:opacity-100 hover:bg-sidebar-accent/80',
							)}>
							<ChevronsUpDown className='size-(--icon-standalone)' />
						</button>
					</PopoverTrigger>
				</div>
				<PopoverContent className='z-100 w-64 p-2 bg-popover border-border' align='start'>
					<ProjectSwitcher
						onProjectSelect={onProjectSelect}
						onSuccess={() => setOpen(false)}
						onCreateProjectClick={() => {
							setOpen(false)
							setShowCreateDialog(true)
						}}
					/>
				</PopoverContent>
			</Popover>
			<CreateProjectDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} onSuccess={() => {}} />
		</>
	)
}
